import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  DataSurface,
  PageShell,
  SegmentedControl,
  cn,
} from "../design-system";
import "./AgentsPage.css";

const MCP_HOST_URL = "https://mcp.botspot.trade";
const MCP_SERVER_URL = "https://mcp.botspot.trade/mcp";

const codeBlocks = {
  claudeCode: `claude mcp add --transport http botspot https://mcp.botspot.trade/mcp --header "Authorization: Bearer $BOTSPOT_API_KEY"`,
  cursor: `{
  "mcpServers": {
    "botspot": {
      "url": "https://mcp.botspot.trade/mcp",
      "headers": {
        "Authorization": "Bearer botspot_YOUR_API_KEY"
      }
    }
  }
}`,
};

type ClientId = "chatgpt" | "claude" | "claude-code" | "cursor";

interface CopyButtonProps {
  value: string;
  label: string;
  className?: string;
  children?: React.ReactNode;
}

interface StepInstruction {
  label: string;
  body: React.ReactNode;
}

interface ConnectClient {
  id: ClientId;
  name: string;
  context: string;
  auth: string;
  bestFor: string;
  copyValue?: string;
  copyLabel?: string;
  code?: string;
  steps?: StepInstruction[];
}

const connectClients: ConnectClient[] = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    context: "OAuth connector",
    auth: "OAuth",
    bestFor: "Asking ChatGPT to inspect BotSpot strategies, broker context, backtests, and deployment state.",
    copyValue: MCP_SERVER_URL,
    copyLabel: "Copy ChatGPT Server URL",
    steps: [
      {
        label: "Check access",
        body: <>Use ChatGPT on the web. Your plan and workspace role must allow custom apps; a workspace admin may need to enable <strong>Developer mode</strong> first.</>,
      },
      {
        label: "Create app",
        body: <>Open <strong>Settings -&gt; Apps</strong>, click <strong>Create</strong>, and keep <strong>Server URL</strong> selected. Workspace admins can also use <strong>Workspace settings -&gt; Apps -&gt; Create</strong>.</>,
      },
      {
        label: "Fill fields",
        body: <>Use <strong>BotSpot</strong> as the name, paste <code>{MCP_SERVER_URL}</code>, and set authentication to <strong>OAuth</strong>.</>,
      },
      {
        label: "Scan and authorize",
        body: <>Click <strong>Scan Tools</strong>, complete the BotSpot OAuth login, wait for the scan to finish, then click <strong>Create</strong>.</>,
      },
      {
        label: "Use it in chat",
        body: "Start a new chat and select BotSpot from the tools menu, or refer to BotSpot in your prompt.",
      },
    ],
  },
  {
    id: "claude",
    name: "Claude",
    context: "Remote MCP",
    auth: "OAuth",
    bestFor: "Keeping Claude connected to the same BotSpot strategy and execution context you use in the app.",
    copyValue: MCP_HOST_URL,
    copyLabel: "Copy Claude MCP URL",
    steps: [
      {
        label: "Open Claude",
        body: <>Go to <strong>Settings -&gt; Connectors</strong> and add a custom web connector.</>,
      },
      {
        label: "Authorize",
        body: "Paste the MCP server URL, complete OAuth, then enable the connector for the conversation.",
      },
    ],
  },
  {
    id: "claude-code",
    name: "Claude Code",
    context: "CLI setup",
    auth: "BotSpot API key",
    bestFor: "Working from a terminal while still referencing your BotSpot library, revisions, and backtest evidence.",
    code: codeBlocks.claudeCode,
    copyLabel: "Copy Claude Code command",
    steps: [
      {
        label: "Create a key",
        body: <>Create a BotSpot API key and export it as <code>BOTSPOT_API_KEY</code>.</>,
      },
      {
        label: "Install MCP",
        body: "Run the command below from your shell.",
      },
    ],
  },
  {
    id: "cursor",
    name: "Cursor",
    context: "IDE setup",
    auth: "BotSpot API key",
    bestFor: "Bringing BotSpot strategy context into a code editor without copying credentials into prompts.",
    code: codeBlocks.cursor,
    copyLabel: "Copy Cursor MCP config",
    steps: [
      {
        label: "Choose scope",
        body: <>Use <code>.cursor/mcp.json</code> for one project or <code>~/.cursor/mcp.json</code> globally.</>,
      },
      {
        label: "Add BotSpot",
        body: "Paste the config below and replace the placeholder with a BotSpot API key.",
      },
    ],
  },
];

function CopyButton({ value, label, className, children }: CopyButtonProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleCopy = async (): Promise<void> => {
    if (!value || !window.navigator.clipboard) return;

    try {
      await window.navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
      return;
    }

    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Button
      aria-label={label}
      className={cn("agents-copy-button", className)}
      onClick={() => { void handleCopy(); }}
      size="sm"
      type="button"
      variant="outline"
    >
      {copied ? "Copied" : children || "Copy"}
    </Button>
  );
}

function CopyField({ value, label }: CopyButtonProps): React.ReactElement {
  return (
    <div className="agents-copy-field">
      <code>{value}</code>
      <CopyButton value={value} label={label} />
    </div>
  );
}

function CodeBlock({ code, label }: { code: string; label: string }): React.ReactElement {
  return (
    <div className="agents-code-block" role="group" aria-label={`${label} snippet`}>
      <div className="agents-code-block__bar">
        <span>{label}</span>
        <CopyButton value={code} label={`Copy ${label}`} />
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

function ClientSelector({
  activeClientId,
  onChange,
}: {
  activeClientId: ClientId;
  onChange: (clientId: ClientId) => void;
}): React.ReactElement {
  return (
    <SegmentedControl
      className="agents-client-selector"
      label="Connect AI client"
      onChange={(value) => onChange(value as ClientId)}
      options={connectClients.map((client) => ({ value: client.id, label: client.name }))}
      value={activeClientId}
    />
  );
}

function ClientPanel({ client }: { client: ConnectClient }): React.ReactElement {
  return (
    <div className="agents-client-panel" role="region" aria-label={`${client.name} setup`} aria-live="polite">
      <div className="agents-client-panel__header">
        <div>
          <span>{client.context}</span>
          <h2>{client.name}</h2>
        </div>
      </div>

      <dl className="agents-client-summary">
        <div>
          <dt>Best for</dt>
          <dd>{client.bestFor}</dd>
        </div>
        <div>
          <dt>Authentication</dt>
          <dd>{client.auth}</dd>
        </div>
      </dl>

      {client.steps && (
        <ol className="agents-step-list">
          {client.steps.map((step) => (
            <li key={step.label}>
              <strong>{step.label}</strong>
              <span>{step.body}</span>
            </li>
          ))}
        </ol>
      )}

      {client.copyValue && client.copyLabel && (
        <div className="agents-connection-block">
          <span>Server URL</span>
          <CopyField value={client.copyValue} label={client.copyLabel} />
        </div>
      )}

      {client.code && client.copyLabel && (
        <CodeBlock code={client.code} label={client.copyLabel.replace("Copy ", "")} />
      )}
    </div>
  );
}

function SetupSurface(): React.ReactElement {
  const [activeClientId, setActiveClientId] = useState<ClientId>("chatgpt");
  const activeClient = connectClients.find((client) => client.id === activeClientId) ?? connectClients[0];

  return (
    <section className="agents-section" aria-label="AI client setup">
      <DataSurface className="agents-setup-surface">
        <ClientSelector activeClientId={activeClient.id} onChange={setActiveClientId} />
        <ClientPanel client={activeClient} />
      </DataSurface>
    </section>
  );
}

export default function AgentsPage(): React.ReactElement {
  return (
    <PageShell className="agents-page" innerClassName="agents-page__inner">
      <SetupSurface />
    </PageShell>
  );
}
